import { locale } from "../i18n";

/* Formatters follow the reader's language: Arabic month names, Western digits, Gregorian dates. */
function dateFmt() {
  return new Intl.DateTimeFormat(locale(), { year: "numeric", month: "short", day: "2-digit" });
}
function dateTimeFmt() {
  return new Intl.DateTimeFormat(locale(), {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : dateFmt().format(d);
}

export function formatDateTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : dateTimeFmt().format(d);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/* ── the aerodrome's clock ────────────────────────────────────────────────────
 *
 * Every time on the flight screens is the time at the aerodrome, not the time
 * where the reader is sitting. A controller in the tower and a duty manager
 * watching from another country must read the same 09:30 off the same screen,
 * or the screen is worse than no screen.
 *
 * This lived as `const OFFSET = 3` copied into four pages, with the domain
 * slicing the raw ISO string instead — which is UTC. So the stand board said
 * FZ813 was on C4 from 12:30 and the conflict panel beside it said the same
 * aircraft was on the same stand from 09:30. Both were on screen at once.
 *
 * One clock, named once. The zone rather than an offset, because an offset is
 * a fact about a date and a zone is a fact about a place — Riyadh happens not
 * to observe summer time, and the next aerodrome will.
 *
 * It belongs to the site, and the platform's `/v1/sites` already returns a
 * `timezone` for each one. The frontend `Site` does not carry it yet; when it
 * does, this constant is the one place to replace.
 */
export const AERODROME_ZONE = "Asia/Riyadh";

/** The same clock as an offset in hours, for the hourly bars. */
export const AERODROME_OFFSET_HOURS = 3;

const timeFmt = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: AERODROME_ZONE,
});

/**
 * HH:MM at the aerodrome, or a dash.
 *
 * Fixed to en-GB rather than the reader's locale: a 24-hour time differs
 * between English and Arabic only in its digits, and these screens have
 * always shown Western digits. Following the locale here would renumber every
 * flight board the moment somebody switched language.
 */
export function hhmm(iso: string | null | undefined, blank = "\u2014"): string {
  if (!iso) return blank;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? blank : timeFmt.format(ms);
}
